import * as React from 'react'

import * as Sentry from '@sentry/browser'
import {
  DATE_FNS_LOCALES_MAPPING,
  MOMENT_LOCALES_MAPPING,
  SUPPORTED_LANGUAGES,
} from 'config/locales'
import { setDefaultOptions } from 'date-fns'
import FactorialI18n from 'factorial-i18n'
import { isNil, merge, result } from 'lodash'
import moment from 'moment'

import type { DayOfWeekIndex } from './dates'

// If there's Sentry we'll send the exception there
const captureRavenException = (path: unknown, error: unknown) => {
  Sentry.setTag('i18n', true)
  Sentry.setExtra('missing_i18n', path)
  Sentry.captureException(error, {})
}

const getLanguageFromLocale = (locale: string) => {
  if (SUPPORTED_LANGUAGES.includes(locale)) return locale

  return locale.split('-')[0]
}

// This is done to "fake" locales into loading data from another locale.
// Temporarily used to map the Greek language to the English one, as we
// need to store the right locale on the DB for the mobile app to use, but
// we won't actually support it on the application.
const LANGUAGE_OVERRIDES: Record<string, string> = {
  el: 'en',
}

type Token =
  | {
      type: 'interpolation'
      interpolation: string
    }
  | {
      type: 'text'
      text: string
    }

interface ViewOptions {
  className?: string
}

class ErrorWithPath extends Error {
  failedPath: string

  constructor(message: string, failedPath: string) {
    super(message)
    this.failedPath = failedPath
  }
}

class I18n extends FactorialI18n {
  raiseI18nExceptions = false

  setLocaleAndMoment(locale: string, firstDayOfWeek?: DayOfWeekIndex): void {
    super.setLocale(getLanguageFromLocale(locale) ?? '')
    super.setLocaleFallback('en')

    // The locale of moment needs to be updated first. If not the date format of the new locale will changed to the previous format.
    // For example if current format was 'en-us' with a date format of 'MM/DD/YYYY' and the new locale is'en-ca', which has a format of 'YYYY-MM-DD'.
    // It was changing the date format of 'en-ca' to 'MM/DD/YYYY'. So we were left with the right locale but the wrong date format.
    // Updating the moment locale first fixes this issue
    // After we can update the first day of week of moment to get proper calendars.
    const momentLocale = MOMENT_LOCALES_MAPPING[locale] || locale

    if (!isNil(firstDayOfWeek)) {
      moment.locale(momentLocale)
      moment.locale(momentLocale, {
        week: {
          dow: firstDayOfWeek,
        },
      })
    } else {
      moment.locale(momentLocale)
    }

    setDefaultOptions({
      locale: DATE_FNS_LOCALES_MAPPING[locale] ?? DATE_FNS_LOCALES_MAPPING['en-us'],
      weekStartsOn: firstDayOfWeek,
    })
  }

  async loadLocale(locale: string, firstDayOfWeek: DayOfWeekIndex = 0): Promise<void> {
    const normalizedLocale = locale.toLowerCase()
    const language = getLanguageFromLocale(normalizedLocale)
    const overriddenLanguage = LANGUAGE_OVERRIDES[language ?? ''] || language
    const data = (await import(`../../../../tmp/translations/${overriddenLanguage}.json`)).default

    this.translations = {
      [language ?? '']: data,
    }

    this.setLocaleAndMoment(normalizedLocale, firstDayOfWeek)
  }

  t(path: string, opts?: Record<string, unknown>): string {
    try {
      return super.t(path, opts)
    } catch (e) {
      if (this.raiseI18nExceptions) {
        throw e
      }

      // If we're called from tp(), let the exception bubble up to trigger plural fallback logic
      // even in production
      if (opts?._allowThrow) {
        throw new ErrorWithPath(e instanceof Error ? e.message : String(e), path)
      }

      captureRavenException(path, e)

      return path
    }
  }

  tp(path: string, opts: Record<string, unknown> = {}): string {
    try {
      // Add flag to opts so t() knows to allow exceptions for fallback logic
      const optsWithAllowThrow = { ...opts, _allowThrow: true }
      return super.tp(path, optsWithAllowThrow)
    } catch (e) {
      if (this.raiseI18nExceptions) {
        throw e
      }

      const failedPath = e instanceof ErrorWithPath ? e.failedPath : path

      captureRavenException(failedPath, e)
      return failedPath
    }
  }

  /**
   * Choose a different copy whether the given user
   * is the current or not. Only accepts `current` and
   * `other` subkeys at the moment.
   */
  tCurrent(path: string, isCurrentUser: boolean, opts?: Record<string, unknown>) {
    const branch = isCurrentUser ? 'current' : 'another'

    return this.tx(path, branch, opts)
  }

  tView(path: string, interpolations: Record<string, React.ReactNode>, opts?: ViewOptions) {
    try {
      const text = super.getKey(path)

      if (typeof text !== 'string') {
        throw new Error(`Key "${path}" is not a leaf`)
      }

      const interpolationPattern = /(%{(.+?)})/

      const tokenize = (): Token[] => {
        if (typeof text !== 'string') return []

        return text
          .split(interpolationPattern)
          .filter((token, i, tokens) => {
            return !(token === '' || interpolationPattern.test(tokens[i - 1] ?? ''))
          })
          .map((token) => {
            if (interpolationPattern.test(token)) {
              return {
                type: 'interpolation',
                interpolation: token,
              }
            }

            return {
              type: 'text',
              text: token,
            }
          })
      }

      const findComponent = (interpolation: string) => {
        const component = interpolations[interpolation]

        if (!component) {
          throw new Error(`No interpolation found for key "${interpolation}" in path "${path}"`)
        }

        return component
      }

      const renderToken = (token: Token, index: number) => {
        if (token.type === 'interpolation') {
          const component = findComponent(
            result(token.interpolation.match(interpolationPattern), '2')
          )

          if (React.isValidElement(component)) {
            return React.cloneElement(component, {
              key: `token${index}`,
            })
          }

          return React.createElement('span', { key: `token${index}` }, component)
        }

        return React.createElement('span', { key: `token${index}` }, token.text)
      }

      /**
       * Tokenizes the translated text ( see `tokenize` ). "text" tokens are replaced
       * for <span> elements, and "interpolation" tokens are replaced for a matching
       * component in the "interpolations" prop.
       */
      const tokens = tokenize()

      return React.createElement('span', { className: opts?.className }, tokens.map(renderToken))
    } catch (e) {
      if (this.raiseI18nExceptions) {
        throw e
      }

      captureRavenException(path, e)

      return path
    }
  }
}

const singleton = new I18n()

export default singleton

if (import.meta.hot) {
  /**
   * This code is removed in production.
   * import file 'en.dev.json' to enable HMR
   * When the file modified, the translations are merged
   */
  const _devTranslations = import('../../../translations/en.dev.json')
  import.meta.hot.accept('../../../translations/en.dev.json', (newModule) => {
    if (newModule && newModule.default) {
      merge(singleton.translations[singleton.locale], newModule.default)
    }
  })
} //end_import_meta
